import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Warehouse, ArrowRight, Scan } from "lucide-react";

export default function TrasferimentoMagazzino() {
    const [pedane, setPedane] = useState([]);
    const [ubic, setUbic] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [pid, setPid] = useState(null);
    const [ubicId, setUbicId] = useState("");
    const [note, setNote] = useState("");
    const [scan, setScan] = useState("");
    const scanRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const [p, u] = await Promise.all([
                api.get("/pedane?tipo_ubicazione=magazzino"),
                api.get("/ubicazioni?tipo=magazzino"),
            ]);
            setPedane(p.data); setUbic(u.data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); scanRef.current?.focus(); }, []);

    const openMove = (p) => { setPid(p); setUbicId(""); setNote(""); setOpen(true); };

    const onScan = (e) => {
        if (e.key !== "Enter") return;
        const code = scan.trim().toUpperCase();
        if (!code) return;
        const found = pedane.find(p => p.numero === code);
        setScan("");
        if (!found) { toast.error(`Pedana ${code} non trovata a magazzino`); return; }
        openMove(found);
    };

    const submit = async () => {
        try {
            await api.post(`/pedane/${pid.id}/move`, { ubicazione_id: ubicId, note: note || null });
            toast.success("Pedana spostata · TM generato");
            setOpen(false); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-6">
            <div>
                <div className="text-[10px] tracking-[0.3em] font-mono text-blue-400/80">MAGAZZINO</div>
                <h1 className="mt-2 font-display text-3xl md:text-4xl font-extrabold">Sposta Pedana</h1>
                <p className="mt-2 text-slate-400 text-sm max-w-2xl">
                    Trasferimento interno di una pedana tra ubicazioni magazzino. Documento <span className="doc-code">TM-2026-XXXX</span>.
                </p>
            </div>

            <Card className="card p-4 border-amber-500/30 bg-amber-500/5">
                <Label className="text-xs text-amber-300 tracking-widest uppercase flex items-center gap-2 mb-2">
                    <Scan size={14} /> Scansiona codice pedana
                </Label>
                <Input ref={scanRef} data-testid="input-scan-pedana" value={scan}
                       onChange={e => setScan(e.target.value)} onKeyDown={onScan}
                       placeholder="PL-2026-0001"
                       className="bg-slate-950/60 border-amber-500/40 h-12 text-lg font-mono tracking-wider" />
                <div className="text-[10px] text-slate-500 mt-1">Scansiona o digita il numero pedana + Invio per aprirla in spostamento</div>
            </Card>

            <Card className="card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-800">
                            <tr>
                                <th className="text-left px-5 py-3">Pedana</th>
                                <th className="text-left px-5 py-3">Ubicazione attuale</th>
                                <th className="text-left px-5 py-3">Contenuto</th>
                                <th className="text-right px-5 py-3">Cartoni</th>
                                <th className="text-right px-5 py-3">Azione</th>
                            </tr>
                        </thead>
                        <tbody data-testid="table-move-pedane">
                            {loading && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-500 text-xs">Caricamento…</td></tr>}
                            {!loading && pedane.length === 0 && (
                                <tr><td colSpan={5} className="px-5 py-12 text-center text-slate-500">
                                    <Warehouse size={36} className="mx-auto mb-3 opacity-40" />
                                    <div className="text-sm">Nessuna pedana a magazzino</div>
                                </td></tr>
                            )}
                            {pedane.map(p => (
                                <tr key={p.id} className="border-b border-slate-800/60 hover:bg-slate-800/30">
                                    <td className="px-5 py-3 doc-code text-xs">{p.numero}</td>
                                    <td className="px-5 py-3 font-mono text-xs text-blue-300">{p.ubicazione?.codice}</td>
                                    <td className="px-5 py-3">
                                        <div className="flex flex-wrap gap-1">
                                            {p.righe.map((r, i) => (
                                                <span key={i} className="text-[10px] font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300">
                                                    {r.codice}×{r.cartoni}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="px-5 py-3 text-right font-mono font-bold">{p.totale_cartoni}</td>
                                    <td className="px-5 py-3 text-right">
                                        <Button size="sm" onClick={()=>openMove(p)} data-testid={`button-move-${p.numero}`}
                                                className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 border border-blue-500/40 gap-2 h-8">
                                            <ArrowRight size={12} /> Sposta
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Card>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
                    <DialogHeader><DialogTitle className="font-display">Sposta pedana</DialogTitle></DialogHeader>
                    {pid && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-sm">
                                <Badge className="badge-pill bg-blue-500/15 text-blue-300 border-blue-500/40">{pid.ubicazione?.codice}</Badge>
                                <ArrowRight size={14} className="text-slate-500" />
                                <Badge className="badge-pill bg-emerald-500/15 text-emerald-300 border-emerald-500/40">?</Badge>
                            </div>
                            <div className="text-sm text-slate-400"><span className="doc-code">{pid.numero}</span> · {pid.totale_cartoni} cart.</div>
                            <div>
                                <Label className="text-xs text-slate-400">Nuova ubicazione *</Label>
                                <Select value={ubicId} onValueChange={setUbicId}>
                                    <SelectTrigger data-testid="select-new-ubic" className="bg-slate-950/50 border-slate-800 mt-1"><SelectValue placeholder="Seleziona ubicazione destinazione…" /></SelectTrigger>
                                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                                        {ubic.filter(u => u.id !== pid.ubicazione?.id).map(u => (
                                            <SelectItem key={u.id} value={u.id}>{u.codice}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs text-slate-400">Note</Label>
                                <Textarea value={note} onChange={e=>setNote(e.target.value)} data-testid="input-move-note"
                                          className="bg-slate-950/50 border-slate-800 mt-1" rows={2} />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={()=>setOpen(false)} className="bg-transparent border-slate-700">Annulla</Button>
                        <Button onClick={submit} data-testid="button-confirm-move" disabled={!ubicId}
                                className="bg-amber-500 hover:bg-amber-400 text-slate-950">Conferma spostamento</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
